import { useState } from 'react';
import type { TenderRecord } from '../../../types/analyticstypes';
import { formatMoney, maskCardNumber, entryModeLabel, formatLocalDateTime } from '../../../utils/analytics/format';

function StatusChip({ tender }: { tender: TenderRecord }) {
  const approved = tender.approved;
  const cls = approved
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      {approved ? 'APPROVED' : tender.status.toUpperCase()}
    </span>
  );
}

export default function TenderCard({ tender }: { tender: TenderRecord }) {
  const [showTechnical, setShowTechnical] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const { brand, last4 } = maskCardNumber(tender.authorization?.cardMask, tender.subType);
  const entryMode = entryModeLabel(tender.processor?.cardEntryMode);
  const auth = tender.authorization;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{brand}</span>
            {last4 && (<span className="font-mono text-gray-600 text-sm">····{last4}</span>)}
            <StatusChip tender={tender} />
          </div>
          <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
            {tender.processor?.cardEntryMode && <span>{entryMode}</span>}
            {auth?.authDate && <span>{formatLocalDateTime(auth.authDate)}</span>}
            {auth?.approvalCode && <span>Auth {auth.approvalCode}</span>}
            {auth?.responseCode && (
              <span>
                Resp {auth.responseCode}
                {auth.responseText ? ` — ${auth.responseText}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-mono font-semibold text-gray-900">
            {formatMoney(tender.amount, tender.currency)}
          </div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide">
            {tender.provider || '—'} {tender.tenderType ? `· ${tender.tenderType}` : ''}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => setShowTechnical((v) => !v)}
          className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
        >
          <svg
            className={`w-3 h-3 transition-transform ${showTechnical ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {showTechnical ? 'Hide' : 'Show'} technical details
        </button>
        {showTechnical && tender.processor?.raw != null && (
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11px] text-gray-500 hover:text-gray-700 font-medium"
          >
            {showRaw ? 'Hide' : 'Show'} raw processor blob
          </button>
        )}
      </div>

      {showTechnical && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 font-mono">
          {tender.processor?.aid && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">AID</span><span className="text-gray-900 truncate" title={tender.processor.aid}>{tender.processor.aid}</span></div>
          )}
          {tender.processor?.ac && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">AC</span><span className="text-gray-900 truncate" title={tender.processor.ac}>{tender.processor.ac}</span></div>
          )}
          {tender.processor?.cardEntryMode && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Entry Mode</span><span className="text-gray-900">{tender.processor.cardEntryMode}</span></div>
          )}
          {tender.processor?.aurusPayTicketNum && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Aurus Ticket</span><span className="text-gray-900 truncate" title={tender.processor.aurusPayTicketNum}>{tender.processor.aurusPayTicketNum}</span></div>
          )}
          {auth?.batchId && (<div className="flex justify-between gap-2"><span className="text-gray-500">Batch</span><span className="text-gray-900">{auth.batchId}</span></div>)}
          {auth?.transactionId && (<div className="flex justify-between gap-2"><span className="text-gray-500">Txn ID</span><span className="text-gray-900 truncate" title={auth.transactionId}>{auth.transactionId}</span></div>)}
          {auth?.referenceNumber && (<div className="flex justify-between gap-2"><span className="text-gray-500">Reference</span><span className="text-gray-900 truncate" title={auth.referenceNumber}>{auth.referenceNumber}</span></div>)}
          {auth?.gateway && (<div className="flex justify-between gap-2"><span className="text-gray-500">Gateway</span><span className="text-gray-900">{auth.gateway}</span></div>)}
          {tender.processor?.emvTags &&
            Object.entries(tender.processor.emvTags).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2"><span className="text-gray-500">{k}</span><span className="text-gray-900 truncate" title={v}>{v}</span></div>
            ))}
        </div>
      )}

      {showRaw && tender.processor?.raw != null && (
        <pre className="mt-3 bg-gray-900 text-gray-100 text-[10px] p-3 rounded overflow-x-auto max-h-[400px] font-mono">
          {JSON.stringify(tender.processor.raw, null, 2)}
        </pre>
      )}
    </div>
  );
}
